import React, { useState, useRef, useEffect } from 'react';
import { X, Save, Play, Pause, Clock, GitBranch, Plus, Trash2, MessageCircle, PhoneCall, Zap, Flag, ChevronUp, ChevronDown } from 'lucide-react';
import { toast } from 'react-toastify';
import FullScreenModal from '../../../components/Modal/FullScreenModal';
import {
  createCampaign,
  updateCampaign,
  getJourneyTriggers,
  JourneyTrigger
} from '@/actions/drip-campaigns';
import { whatsappCloudService, WhatsAppCloudTemplate } from '@/services/integrations/whatsappCloudService';

// Visual builder step types. 'email'/'delay'/'conditional' are the originals;
// 'whatsapp' and 'auto_call' are content channels added for Product Journeys.
// Email is deliberately absent: there are no email templates in the system, so an
// email node could only ever fail at send time. Auto-dial is absent too -- the dialer
// is not wired to journeys, so a call node raises a reminder for an agent to call
// manually rather than pretending the system will dial.
type StepType = 'delay' | 'conditional' | 'whatsapp' | 'agent_call' | 'exit';

// Map visual step type -> backend node_type (drip_campaign_steps.node_type).
const NODE_TYPE_BY_STEP: Record<StepType, string> = {
  whatsapp: 'whatsapp',
  agent_call: 'agent_call',
  delay: 'delay',
  conditional: 'condition',
  exit: 'exit',
};

// Backend node_type -> visual type. Legacy values ('auto_dial' from before call nodes
// became agent reminders, 'email' from the old drip builder) still load so an existing
// flow can be opened and re-saved rather than silently losing steps.
const STEP_BY_NODE_TYPE: Record<string, StepType> = {
  whatsapp: 'whatsapp',
  agent_call: 'agent_call',
  auto_dial: 'agent_call',
  ai_call: 'agent_call',
  delay: 'delay',
  condition: 'conditional',
  exit: 'exit',
  email: 'whatsapp',
};

// Human labels per step type (toasts, node titles, add-menus).
const STEP_LABEL: Record<StepType, string> = {
  whatsapp: 'WhatsApp step',
  agent_call: 'Agent call reminder',
  delay: 'Delay',
  conditional: 'Conditional branch',
  exit: 'End journey',
};

// Variables the send engine can resolve from a product-journey enrollment.
// Used to map WhatsApp template {{1}}/{{2}} params without free-text errors.
const JOURNEY_VARIABLES: { key: string; label: string }[] = [
  { key: 'customer_name', label: 'Customer name' },
  { key: 'customer_phone', label: 'Customer phone' },
  { key: 'product_title', label: 'Product title' },
  { key: 'order_number', label: 'Order number' },
  { key: 'order_date', label: 'Order date' },
  { key: 'delivered_date', label: 'Delivered date' },
  { key: 'reorder_due_date', label: 'Reorder due date' },
  { key: 'tracking_url', label: 'Tracking URL' },
  { key: 'discount_code', label: 'Discount code' },
];

// Where a step's wait is measured from. The order anchors only make sense on a journey
// whose trigger supplies an order, which the trigger strip enforces.
const DELAY_ANCHORS: { value: string; label: string }[] = [
  { value: 'previous_step', label: 'the previous step' },
  { value: 'enrollment', label: 'the journey starting' },
  { value: 'order_delivered', label: 'the order being delivered' },
  { value: 'reorder_due_date', label: 'the reorder due date' },
];

// Branch conditions the engine can evaluate. Each names the signal it waits on.
const CONDITION_TYPES: { value: string; label: string; help: string }[] = [
  { value: 'call_connected', label: 'Call was answered',
    help: 'YES if the agent reached the customer, NO if missed / not answered / not attempted.' },
  { value: 'whatsapp_replied', label: 'Customer replied on WhatsApp',
    help: 'YES if any reply arrives within the wait window.' },
  { value: 'reordered', label: 'Customer reordered',
    help: 'YES if a new order is placed within the wait window.' },
  { value: 'disposition_is', label: 'Disposition matches',
    help: 'YES if the agent set the disposition you choose.' },
  { value: 'time_based', label: 'Just wait',
    help: 'Always takes the YES path once the wait elapses.' },
];

// Sensible default `data` for a freshly-created step of each type.
const defaultDataForType = (type: StepType): any => {
  switch (type) {
    case 'whatsapp':
      return { stepName: '', waTemplateId: null, waTemplateName: '', waLanguage: 'en', waParams: [] };
    case 'agent_call':
      return { stepName: '', rrBucket: 'reorder', note: '' };
    case 'delay':
      return { delayValue: 1, delayUnit: 'days', delayFrom: 'previous_step' };
    case 'conditional':
      return { conditionType: 'call_connected', waitHours: 24 };
    case 'exit':
      return { stepName: 'End journey' };
    default:
      return { stepName: '' };
  }
};

interface CampaignStep {
  id: string;
  type: StepType;
  order: number;
  position: { x: number; y: number };
  data: any;
  nextStepId?: string; // For regular flow
  yesStepId?: string; // For conditional YES branch
  noStepId?: string; // For conditional NO branch
}

interface Campaign {
  id?: number;
  name: string;
  description?: string;
  status: 'draft' | 'active' | 'paused';
  type: 'drip' | 'status_change';
  steps: CampaignStep[];
  // What starts the flow. `anchorEvent` is a key from the API trigger catalogue,
  // `triggerConfig` holds that trigger's own settings, and `enrollmentType` decides
  // whether the event fires automatically or leads are added by hand.
  anchorEvent?: string;
  triggerConfig?: Record<string, any>;
  enrollmentType?: 'auto_trigger' | 'manual';
}

interface CampaignBuilderModalProps {
  isOpen: boolean;
  onClose: () => void;
  campaign?: any; // Existing campaign from API
  onSuccess: () => void;
}

const CampaignBuilderModal: React.FC<CampaignBuilderModalProps> = ({
  isOpen,
  onClose,
  campaign: initialCampaign,
  onSuccess
}) => {
  const [campaign, setCampaign] = useState<Campaign>({
    name: 'New Campaign',
    description: '',
    status: 'draft',
    type: 'drip',
    steps: []
  });

  const [selectedStep, setSelectedStep] = useState<CampaignStep | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [waTemplates, setWaTemplates] = useState<WhatsAppCloudTemplate[]>([]);
  const [triggers, setTriggers] = useState<JourneyTrigger[]>([]);

  // Load templates and campaign data
  useEffect(() => {
    if (isOpen) {
      loadTemplates();

      if (initialCampaign) {
        // Transform API campaign to visual builder format
        const transformedCampaign = transformApiToVisual(initialCampaign);
        setCampaign(transformedCampaign);
      } else {
        // Reset for new campaign
        setCampaign({
          name: 'New Campaign',
          description: '',
          status: 'draft',
          type: 'drip',
          steps: []
        });
      }
      setSelectedStep(null);
    }
  }, [isOpen, initialCampaign]);

  const loadTemplates = async () => {
    try {
      setIsLoading(true);
      const [waList, trigRes] = await Promise.all([
        whatsappCloudService.getTemplates().catch(() => []),
        getJourneyTriggers().catch(() => null),
      ]);
      // Only approved WhatsApp templates can be sent -- and not ones whose content the
      // system generates per send (e.g. a Razorpay link), which a journey cannot supply.
      setWaTemplates((waList || []).filter((t) => t.status === 'APPROVED' && t.manual_send !== false));
      if (trigRes?.success && Array.isArray(trigRes.data)) {
        setTriggers(trigRes.data.filter((t) => t.available));
      }
    } catch (error) {
      console.error('Error loading templates:', error);
      toast.error('Failed to load templates');
    } finally {
      setIsLoading(false);
    }
  };

  // ===== API <-> visual graph =====
  // Every node is persisted 1:1, including delay and branch nodes. The previous
  // version saved only message steps and derived delays from the node that followed,
  // which silently discarded every conditional branch drawn on the canvas.

  const transformApiToVisual = (apiCampaign: any): Campaign => {
    const apiSteps = [...(apiCampaign.steps || [])].sort(
      (a: any, b: any) => a.step_order - b.step_order
    );

    const visualIdByApiId = new Map<number, string>();
    apiSteps.forEach((s: any) => visualIdByApiId.set(s.id, `step-${s.id}`));

    const visualSteps: CampaignStep[] = apiSteps.map((step: any, index: number) => {
      const type: StepType = STEP_BY_NODE_TYPE[step.node_type] || 'whatsapp';

      let data: any;
      switch (type) {
        case 'whatsapp':
          data = {
            stepId: step.id,
            stepName: step.step_name,
            waTemplateId: step.integration_template_id_fk || null,
            waTemplateName: step.whatsapp_template_name || '',
            waLanguage: step.whatsapp_template_language || 'en',
            waParams: step.whatsapp_template_params || [],
          };
          break;
        case 'agent_call':
          data = {
            stepId: step.id,
            stepName: step.step_name,
            rrBucket: step.config?.rr_bucket || 'reorder',
            note: step.config?.note || '',
          };
          break;
        case 'delay':
          data = {
            stepId: step.id,
            delayValue: step.delay_value ?? 1,
            delayUnit: step.delay_unit || 'days',
            delayFrom: step.delay_from || 'previous_step',
          };
          break;
        case 'conditional':
          data = {
            stepId: step.id,
            stepName: step.step_name,
            conditionType: step.condition_type || 'call_connected',
            waitHours: step.condition_wait_hours ?? 24,
            dispositionOptionId: step.condition_config?.disposition_option_id ?? null,
          };
          break;
        default:
          data = { stepId: step.id, stepName: step.step_name };
      }

      return {
        id: `step-${step.id}`,
        type,
        order: step.step_order,
        position: { x: 120, y: 80 + index * 190 },
        data,
      };
    });

    // Rebuild links: branches from the stored pointers, everything else linear.
    visualSteps.forEach((vs, i) => {
      const api: any = apiSteps[i];
      if (vs.type === 'conditional') {
        vs.yesStepId = api.yes_branch_step_id
          ? visualIdByApiId.get(api.yes_branch_step_id) : undefined;
        vs.noStepId = api.no_branch_step_id
          ? visualIdByApiId.get(api.no_branch_step_id) : undefined;
      } else if (vs.type !== 'exit' && i < visualSteps.length - 1) {
        vs.nextStepId = visualSteps[i + 1].id;
      }
    });

    return {
      id: apiCampaign.id,
      name: apiCampaign.campaign_name || 'New Campaign',
      description: apiCampaign.description || '',
      status: apiCampaign.status || 'draft',
      type: apiCampaign.enrollment_type === 'auto_trigger' ? 'status_change' : 'drip',
      steps: visualSteps,
      anchorEvent: apiCampaign.anchor_event || 'manual',
      triggerConfig: apiCampaign.auto_enrollment_trigger || {},
      enrollmentType: apiCampaign.enrollment_type === 'auto_trigger' ? 'auto_trigger' : 'manual',
    };
  };

  const transformVisualToApi = () => {
    if (!campaign.name.trim()) {
      throw new Error('Please enter a campaign name');
    }
    if (campaign.steps.length === 0) {
      throw new Error('Please add at least one step to your campaign');
    }

    const ordered = [...campaign.steps].sort((a, b) => a.order - b.order);

    // Branch targets are referenced by step_order: the builder cannot know the new
    // primary keys, because every save deletes and re-inserts the rows.
    const orderByVisualId = new Map<string, number>();
    ordered.forEach((s, i) => orderByVisualId.set(s.id, i + 1));

    const apiSteps = ordered.map((step, index) => {
      const base: any = {
        step_name: step.data?.stepName || STEP_LABEL[step.type] || `Step ${index + 1}`,
        step_order: index + 1,
        node_type: NODE_TYPE_BY_STEP[step.type],
        template_id_fk: null,
        delay_value: 0,
        delay_unit: 'days',
        delay_from: index === 0 ? 'enrollment' : 'previous_step',
        parameter_mapping: {},
      };

      switch (step.type) {
        case 'whatsapp': {
          if (!step.data?.waTemplateId) {
            throw new Error(`"${base.step_name}" needs a WhatsApp template selected`);
          }
          return {
            ...base,
            integration_template_id_fk: step.data.waTemplateId,
            whatsapp_template_name: step.data.waTemplateName || null,
            whatsapp_template_language: step.data.waLanguage || 'en',
            whatsapp_template_params: step.data.waParams || [],
          };
        }

        case 'agent_call':
          return {
            ...base,
            config: {
              rr_bucket: step.data?.rrBucket || 'reorder',
              note: step.data?.note || '',
            },
          };

        case 'delay':
          return {
            ...base,
            delay_value: step.data?.delayValue ?? 1,
            delay_unit: step.data?.delayUnit || 'days',
            delay_from: step.data?.delayFrom || 'previous_step',
          };

        case 'conditional': {
          const yes = step.yesStepId ? orderByVisualId.get(step.yesStepId) : undefined;
          const no = step.noStepId ? orderByVisualId.get(step.noStepId) : undefined;
          if (yes === undefined && no === undefined) {
            throw new Error(`"${base.step_name}" needs at least one branch connected`);
          }
          return {
            ...base,
            is_conditional: true,
            condition_type: step.data?.conditionType || 'call_connected',
            condition_wait_hours: step.data?.waitHours ?? 24,
            condition_config: step.data?.dispositionOptionId
              ? { disposition_option_id: step.data.dispositionOptionId }
              : null,
            yes_branch_step_order: yes ?? null,
            no_branch_step_order: no ?? null,
          };
        }

        default:
          return base;
      }
    });

    return {
      campaign_name: campaign.name,
      description: campaign.description,
      goal: initialCampaign?.goal ?? '',
      // Trigger is edited in the builder now, so it is sent rather than preserved.
      anchor_event: campaign.anchorEvent ?? 'manual',
      auto_enrollment_trigger: campaign.triggerConfig ?? {},
      enrollment_type: campaign.enrollmentType
        ?? initialCampaign?.enrollment_type ?? ('manual' as const),
      send_time_preference: initialCampaign?.send_time_preference ?? ('business_hours' as const),
      business_hours_start: initialCampaign?.business_hours_start ?? '09:00:00',
      business_hours_end: initialCampaign?.business_hours_end ?? '17:00:00',
      timezone: initialCampaign?.timezone ?? 'Asia/Kolkata',
      allow_re_enrollment: initialCampaign?.allow_re_enrollment ?? false,
      steps: apiSteps
    };
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      // Transform and validate
      const apiPayload = transformVisualToApi();

      // Save to API
      if (campaign.id) {
        // Update existing campaign
        await updateCampaign(campaign.id, apiPayload as any);
        toast.success('Campaign updated successfully!');
      } else {
        // Create new campaign
        await createCampaign(apiPayload as any);
        toast.success('Campaign created successfully!');
      }

      onSuccess();
      onClose();
    } catch (error: any) {
      console.error('Error saving campaign:', error);
      toast.error(error.message || 'Failed to save campaign');
    } finally {
      setIsSaving(false);
    }
  };

  // Delete selected step
  // ===== Step list operations =====
  // The flow is a list, so order in the array IS the order of execution. There is no
  // nextStepId to maintain -- only branch targets, which point at specific steps.

  const renumber = (steps: CampaignStep[]): CampaignStep[] =>
    steps.map((s, i) => ({ ...s, order: i + 1, position: { x: 120, y: 80 + i * 190 } }));

  const insertStepAt = (index: number, stepType: StepType) => {
    const newStep: CampaignStep = {
      id: `step-${Date.now()}`,
      type: stepType,
      order: index + 1,
      position: { x: 120, y: 80 },
      data: defaultDataForType(stepType),
    };
    setCampaign((prev) => {
      const next = [...prev.steps];
      next.splice(index, 0, newStep);
      return { ...prev, steps: renumber(next) };
    });
    setSelectedStep(newStep);
    toast.success(`${STEP_LABEL[stepType] || 'Step'} added`);
  };

  const handleDeleteStepById = (id: string) => {
    const step = campaign.steps.find((s) => s.id === id);
    if (!step) return;
    if (!confirm(`Delete "${step.data?.stepName || STEP_LABEL[step.type]}"?`)) return;

    setCampaign((prev) => {
      // Any branch pointing at the removed step loses that target, otherwise the save
      // would reference a step that no longer exists.
      const kept = prev.steps
        .filter((s) => s.id !== id)
        .map((s) => ({
          ...s,
          yesStepId: s.yesStepId === id ? undefined : s.yesStepId,
          noStepId: s.noStepId === id ? undefined : s.noStepId,
        }));
      return { ...prev, steps: renumber(kept) };
    });

    if (selectedStep?.id === id) setSelectedStep(null);
    toast.success('Step deleted');
  };

  const handleDeleteStep = () => {
    if (selectedStep) handleDeleteStepById(selectedStep.id);
  };

  const moveStep = (id: string, direction: -1 | 1) => {
    setCampaign((prev) => {
      const i = prev.steps.findIndex((s) => s.id === id);
      const j = i + direction;
      if (i < 0 || j < 0 || j >= prev.steps.length) return prev;
      const next = [...prev.steps];
      [next[i], next[j]] = [next[j], next[i]];
      return { ...prev, steps: renumber(next) };
    });
  };

  const handleUpdateStepWithCreate = (updatedStep: CampaignStep) => {
    setCampaign((prev) => ({
      ...prev,
      steps: prev.steps.map((s) => (s.id === updatedStep.id ? updatedStep : s)),
    }));
    setSelectedStep(updatedStep);
  };

  const toggleCampaignStatus = () => {
    setCampaign(prev => ({
      ...prev,
      status: prev.status === 'active' ? 'paused' : 'active'
    }));
  };



  return (
    <FullScreenModal isOpen={isOpen} onClose={onClose}>
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900">
        <div className="flex items-center gap-4 flex-1">
          <input
            type="text"
            value={campaign.name}
            onChange={(e) => setCampaign(prev => ({ ...prev, name: e.target.value }))}
            className="text-xl font-semibold text-gray-900 dark:text-white bg-transparent border-b-2 border-transparent hover:border-gray-300 focus:border-blue-500 focus:outline-none transition-colors px-2 py-1"
            placeholder="Campaign Name"
          />
          <span className={`px-2 py-1 text-xs font-medium rounded-full ${
            campaign.status === 'active'
              ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
              : campaign.status === 'paused'
              ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
              : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
          }`}>
            {(campaign.status || 'draft').toUpperCase()}
          </span>
        </div>

        <div className="flex items-center gap-2">
          {campaign.id && (
            <button
              onClick={toggleCampaignStatus}
              className={`flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                campaign.status === 'active'
                  ? 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200 dark:bg-yellow-900 dark:text-yellow-200'
                  : 'bg-green-100 text-green-800 hover:bg-green-200 dark:bg-green-900 dark:text-green-200'
              }`}
            >
              {campaign.status === 'active' ? (
                <>
                  <Pause className="w-4 h-4" />
                  Pause
                </>
              ) : (
                <>
                  <Play className="w-4 h-4" />
                  Activate
                </>
              )}
            </button>
          )}

          <button
            onClick={handleSave}
            disabled={isSaving || isLoading}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <Save className="w-4 h-4" />
            {isSaving ? 'Saving...' : 'Save Campaign'}
          </button>

          <button
            onClick={onClose}
            className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Main Content - Two Panel Layout (canvas + properties) */}
      <div className="flex flex-1 overflow-hidden">
        <div className="flex flex-col flex-1 overflow-hidden">
          {/* Trigger strip — every flow starts here, so it sits above the canvas
              rather than being a draggable node that could be deleted. */}
          <TriggerBar
            triggers={triggers}
            anchorEvent={campaign.anchorEvent || 'manual'}
            triggerConfig={campaign.triggerConfig || {}}
            enrollmentType={campaign.enrollmentType || 'manual'}
            onChange={(patch) => setCampaign((prev) => ({ ...prev, ...patch }))}
          />

          {/* Flow list -- order in the list is the order of execution */}
          <FlowList
            steps={campaign.steps}
            selectedStep={selectedStep}
            onSelect={setSelectedStep}
            onInsertAt={insertStepAt}
            onDelete={handleDeleteStepById}
            onMove={moveStep}
          />
        </div>

        {/* Right Panel - Properties Editor */}
        <RightPanel
          selectedStep={selectedStep}
          allSteps={campaign.steps}
          waTemplates={waTemplates}
          onUpdateStep={handleUpdateStepWithCreate}
          onDeleteStep={handleDeleteStep}
          onClose={() => setSelectedStep(null)}
        />
      </div>
    </FullScreenModal>
  );
};

// ===== Trigger strip =====
// Shows what starts the flow and lets it be changed. The trigger list comes from the
// API catalogue, so this renders whatever the engine can actually fire — there is no
// hardcoded list here to drift out of sync.
interface TriggerBarProps {
  triggers: JourneyTrigger[];
  anchorEvent: string;
  triggerConfig: Record<string, any>;
  enrollmentType: 'auto_trigger' | 'manual';
  onChange: (patch: Partial<Campaign>) => void;
}

const TriggerBar: React.FC<TriggerBarProps> = ({
  triggers, anchorEvent, triggerConfig, enrollmentType, onChange,
}) => {
  const selected = triggers.find((t) => t.key === anchorEvent);
  const isManual = anchorEvent === 'manual';

  const setConfigField = (name: string, value: any) => {
    onChange({ triggerConfig: { ...triggerConfig, [name]: value } });
  };

  const pickTrigger = (key: string) => {
    // Config belongs to the trigger that defined it, so drop it when the trigger
    // changes rather than carrying stale keys into the new one. 'manual' never
    // auto-enrolls, so the enrollment mode follows the choice.
    onChange({
      anchorEvent: key,
      triggerConfig: {},
      enrollmentType: key === 'manual' ? 'manual' : 'auto_trigger',
    });
  };

  return (
    <div className="border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-6 py-3">
      <div className="flex items-start gap-4 flex-wrap">
        <div className="flex items-center gap-2 pt-1.5">
          <span className="flex items-center justify-center w-7 h-7 rounded-full bg-amber-100 dark:bg-amber-900/40">
            <Zap className="w-4 h-4 text-amber-600 dark:text-amber-400" />
          </span>
          <span className="text-sm font-semibold text-gray-900 dark:text-white whitespace-nowrap">
            Starts when
          </span>
        </div>

        <div className="min-w-[220px]">
          <select
            value={anchorEvent}
            onChange={(e) => pickTrigger(e.target.value)}
            className="w-full px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {triggers.length === 0 && <option value={anchorEvent}>Loading triggers…</option>}
            {triggers.map((t) => (
              <option key={t.key} value={t.key}>{t.label}</option>
            ))}
          </select>
          {selected && (
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 max-w-md">
              {selected.description}
            </p>
          )}
        </div>

        {/* Trigger-specific settings, rendered from the catalogue's field defs. */}
        {(selected?.config || []).map((field) => (
          <div key={field.name} className="min-w-[180px]">
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
              {field.label}
            </label>
            {field.type === 'select' ? (
              <select
                value={String(triggerConfig[field.name] ?? field.default ?? '')}
                onChange={(e) => setConfigField(field.name, e.target.value)}
                className="w-full px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {(field.options || []).map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            ) : (
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  min={field.min ?? 0}
                  max={field.max ?? 365}
                  value={String(triggerConfig[field.name] ?? field.default ?? 0)}
                  onChange={(e) => setConfigField(field.name, Number(e.target.value))}
                  className="w-20 px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <span className="text-xs text-gray-500 dark:text-gray-400">days</span>
              </div>
            )}
          </div>
        ))}

        {/* Manual triggers cannot auto-enrol, so the toggle is only meaningful otherwise. */}
        {!isManual && (
          <label className="flex items-center gap-2 pt-6 cursor-pointer">
            <input
              type="checkbox"
              checked={enrollmentType === 'auto_trigger'}
              onChange={(e) => onChange({ enrollmentType: e.target.checked ? 'auto_trigger' : 'manual' })}
              className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <span className="text-sm text-gray-700 dark:text-gray-300">Enrol automatically</span>
          </label>
        )}
      </div>

      {!isManual && enrollmentType === 'manual' && (
        <p className="text-xs text-amber-600 dark:text-amber-400 mt-2">
          Auto-enrol is off — this event will not start the flow. Leads must be added by hand.
        </p>
      )}
    </div>
  );
};

// Step-type options for the "add step" menus (empty canvas + on-card "+").
const STEP_TYPE_OPTIONS: { type: StepType; icon: React.ComponentType<any>; label: string; color: string }[] = [
  { type: 'whatsapp', icon: MessageCircle, label: 'WhatsApp step', color: 'green' },
  { type: 'agent_call', icon: PhoneCall, label: 'Agent call reminder', color: 'teal' },
  { type: 'delay', icon: Clock, label: 'Delay', color: 'purple' },
  { type: 'conditional', icon: GitBranch, label: 'Conditional branch', color: 'orange' },
  { type: 'exit', icon: Flag, label: 'End journey', color: 'gray' },
];

// Small dropdown menu that lists the 5 step types. Used by the empty-canvas
// "Add first step" button and each card's "+ Add next step" button.
const AddStepMenu: React.FC<{
  onPick: (type: StepType) => void;
  buttonClassName: string;
  buttonContent: React.ReactNode;
  align?: 'left' | 'center';
  fullWidth?: boolean;
}> = ({ onPick, buttonClassName, buttonContent, align = 'left', fullWidth = false }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  return (
    <div
      ref={ref}
      className={`relative ${fullWidth ? 'block w-full' : 'inline-block'}`}
      onClick={(e) => e.stopPropagation()}
    >
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setOpen(o => !o); }}
        className={buttonClassName}
      >
        {buttonContent}
      </button>
      {open && (
        <div
          className={`absolute z-20 mt-1 w-52 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg py-1 ${
            align === 'center' ? 'left-1/2 -translate-x-1/2' : 'left-0'
          }`}
        >
          {STEP_TYPE_OPTIONS.map(({ type, icon: Icon, label, color }) => (
            <button
              key={type}
              type="button"
              onClick={(e) => { e.stopPropagation(); setOpen(false); onPick(type); }}
              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-left text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            >
              <span className={`p-1 bg-${color}-100 dark:bg-${color}-900 rounded`}>
                <Icon className={`w-3.5 h-3.5 text-${color}-600 dark:text-${color}-400`} />
              </span>
              {label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// ===== Flow list =====
// Replaces the canvas. These journeys are a linear spine with occasional two-way
// branches, so a list expresses them exactly -- and the canvas never persisted node
// positions anyway (they were recomputed from the index on every load), which made
// dragging impossible to keep. Order in this list is the order of execution.

const summarise = (step: CampaignStep): string => {
  switch (step.type) {
    case 'whatsapp':
      return step.data?.waTemplateName || 'No template selected';
    case 'agent_call':
      return `${(step.data?.rrBucket || 'reorder').replace('_', ' ')} team`;
    case 'delay': {
      const anchor = DELAY_ANCHORS.find(
        (a) => a.value === (step.data?.delayFrom || 'previous_step'));
      return `Wait ${step.data?.delayValue ?? 1} ${step.data?.delayUnit || 'days'} from ${anchor?.label}`;
    }
    case 'conditional': {
      const c = CONDITION_TYPES.find((x) => x.value === (step.data?.conditionType));
      return c ? c.label : 'Branch';
    }
    case 'exit':
      return 'Enrolment completes';
    default:
      return '';
  }
};

const STEP_ICON: Record<StepType, React.ComponentType<any>> = {
  whatsapp: MessageCircle,
  agent_call: PhoneCall,
  delay: Clock,
  conditional: GitBranch,
  exit: Flag,
};

const STEP_TONE: Record<StepType, string> = {
  whatsapp: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300',
  agent_call: 'bg-teal-100 text-teal-700 dark:bg-teal-900/40 dark:text-teal-300',
  delay: 'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300',
  conditional: 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300',
  exit: 'bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
};

// The "+" between every pair of steps. Visible at rest -- an affordance that only
// appears on hover cannot be found by someone who does not already know it is there.
const InsertHere: React.FC<{ onPick: (t: StepType) => void }> = ({ onPick }) => (
  <div className="flex justify-center">
    <AddStepMenu
      onPick={onPick}
      align="center"
      fullWidth
      buttonClassName="group my-1 flex h-7 w-full items-center justify-center"
      buttonContent={
        <span className="flex w-full items-center gap-2">
          <span className="h-px flex-1 bg-gray-300 dark:bg-gray-600 group-hover:bg-blue-400 dark:group-hover:bg-blue-600 transition-colors" />
          <span className="inline-flex items-center gap-1 rounded-full border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-2 py-0.5 text-[11px] font-medium text-gray-500 dark:text-gray-400 group-hover:border-blue-400 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
            <Plus className="w-3 h-3" /> Insert step
          </span>
          <span className="h-px flex-1 bg-gray-300 dark:bg-gray-600 group-hover:bg-blue-400 dark:group-hover:bg-blue-600 transition-colors" />
        </span>
      }
    />
  </div>
);

// Primary, always-obvious way to extend the flow: a solid button under the last step.
const AddStepButton: React.FC<{ onPick: (t: StepType) => void; label: string }> = ({ onPick, label }) => (
  <div className="flex justify-center pt-2">
    <AddStepMenu
      onPick={onPick}
      align="center"
      buttonClassName="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors shadow-sm"
      buttonContent={<><Plus className="w-4 h-4" /> {label}</>}
    />
  </div>
);

interface FlowListProps {
  steps: CampaignStep[];
  selectedStep: CampaignStep | null;
  onSelect: (s: CampaignStep) => void;
  onInsertAt: (index: number, t: StepType) => void;
  onDelete: (id: string) => void;
  onMove: (id: string, dir: -1 | 1) => void;
}

const FlowList: React.FC<FlowListProps> = ({
  steps, selectedStep, onSelect, onInsertAt, onDelete, onMove,
}) => {
  const orderOf = (id?: string) =>
    id ? steps.findIndex((s) => s.id === id) + 1 : 0;

  if (steps.length === 0) {
    return (
      <div className="flex-1 overflow-y-auto bg-gray-50 dark:bg-gray-800 p-8">
        <div className="max-w-xl mx-auto text-center py-16">
          <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 flex items-center justify-center">
            <Plus className="w-6 h-6 text-gray-400" />
          </div>
          <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
            This journey has no steps yet.
          </p>
          <AddStepButton onPick={(t) => onInsertAt(0, t)} label="Add first step" />
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-y-auto bg-gray-50 dark:bg-gray-800 p-6">
      <div className="max-w-xl mx-auto">
        <InsertHere onPick={(t) => onInsertAt(0, t)} />

        {steps.map((step, index) => {
          const Icon = STEP_ICON[step.type] || MessageCircle;
          const isSelected = selectedStep?.id === step.id;
          const isBranch = step.type === 'conditional';

          return (
            <React.Fragment key={step.id}>
              <div
                onClick={() => onSelect(step)}
                className={`group relative rounded-xl border bg-white dark:bg-gray-900 p-3 cursor-pointer transition-all ${
                  isSelected
                    ? 'border-blue-500 ring-2 ring-blue-500/20'
                    : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                }`}
              >
                <div className="flex items-center gap-3">
                  <span className="w-6 text-xs font-medium text-gray-400 tabular-nums text-right">
                    {index + 1}
                  </span>
                  <span className={`flex items-center justify-center w-9 h-9 rounded-lg flex-shrink-0 ${STEP_TONE[step.type]}`}>
                    <Icon className="w-4.5 h-4.5" />
                  </span>

                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                      {step.data?.stepName || STEP_LABEL[step.type]}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                      {summarise(step)}
                    </p>
                  </div>

                  <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity">
                    <button
                      onClick={(e) => { e.stopPropagation(); onMove(step.id, -1); }}
                      disabled={index === 0}
                      title="Move up"
                      className="p-1.5 text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 rounded disabled:opacity-30 disabled:cursor-not-allowed"
                    >
                      <ChevronUp className="w-4 h-4" />
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); onMove(step.id, 1); }}
                      disabled={index === steps.length - 1}
                      title="Move down"
                      className="p-1.5 text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 rounded disabled:opacity-30 disabled:cursor-not-allowed"
                    >
                      <ChevronDown className="w-4 h-4" />
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); onDelete(step.id); }}
                      title="Delete step"
                      className="p-1.5 text-gray-400 hover:text-red-600 rounded"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {/* A branch shows where each path goes, by step number. */}
                {isBranch && (
                  <div className="mt-2 ml-9 pl-6 border-l-2 border-orange-200 dark:border-orange-800 space-y-1">
                    {([['YES', step.yesStepId, 'text-green-600 dark:text-green-400'],
                       ['NO', step.noStepId, 'text-red-600 dark:text-red-400']] as const).map(
                      ([label, target, tone]) => (
                        <p key={label} className="text-xs text-gray-500 dark:text-gray-400">
                          <span className={`font-semibold ${tone}`}>{label}</span>
                          {' → '}
                          {target && orderOf(target)
                            ? `step ${orderOf(target)}`
                            : <span className="text-amber-600 dark:text-amber-400">not set</span>}
                        </p>
                      )
                    )}
                  </div>
                )}
              </div>

              {index < steps.length - 1 && (
                <InsertHere onPick={(t) => onInsertAt(index + 1, t)} />
              )}
            </React.Fragment>
          );
        })}

        <AddStepButton onPick={(t) => onInsertAt(steps.length, t)} label="Add step" />
      </div>
    </div>
  );
};

// Right Panel Component - Properties Editor
interface RightPanelProps {
  selectedStep: CampaignStep | null;
  onUpdateStep: (step: CampaignStep) => void;
  onDeleteStep: () => void;
  onClose: () => void;
  allSteps: CampaignStep[];
  waTemplates: WhatsAppCloudTemplate[];
}

const RightPanel: React.FC<RightPanelProps> = ({ selectedStep, onUpdateStep, onDeleteStep, onClose, allSteps, waTemplates }) => {
  if (!selectedStep) {
    return (
      <div className="w-[440px] border-l border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 overflow-y-auto">
        <div className="flex items-center justify-center h-full p-8 text-center">
          <div>
            <div className="w-12 h-12 mx-auto mb-3 bg-gray-200 dark:bg-gray-700 rounded-full flex items-center justify-center">
              <MessageCircle className="w-6 h-6 text-gray-400 dark:text-gray-500" />
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Select a step to edit its properties
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-[440px] border-l border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 overflow-y-auto">
      <div className="p-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
            Step Properties
          </h3>
          <div className="flex items-center gap-1">
            <button
              onClick={onDeleteStep}
              className="p-1 text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 rounded"
              title="Delete step"
            >
              <Trash2 className="w-4 h-4" />
            </button>
            <button
              onClick={onClose}
              className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 rounded"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Step Type Badge */}
        <div className="mb-4">
          <span className="inline-flex items-center gap-2 px-3 py-1.5 text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 rounded-full">
            {selectedStep.type === 'whatsapp' ? <MessageCircle className="w-3 h-3" /> :
             selectedStep.type === 'agent_call' ? <PhoneCall className="w-3 h-3" /> :
             selectedStep.type === 'delay' ? <Clock className="w-3 h-3" /> :
             selectedStep.type === 'exit' ? <Flag className="w-3 h-3" /> :
             <GitBranch className="w-3 h-3" />}
            {STEP_LABEL[selectedStep.type]}
          </span>
        </div>

        {/* Properties Form */}
        <div className="space-y-4">
          {selectedStep.type === 'whatsapp' && (
            <WhatsAppStepProperties step={selectedStep} onUpdate={onUpdateStep} waTemplates={waTemplates} />
          )}
          {selectedStep.type === 'agent_call' && (
            <AgentCallStepProperties step={selectedStep} onUpdate={onUpdateStep} />
          )}
          {selectedStep.type === 'delay' && (
            <DelayStepProperties step={selectedStep} onUpdate={onUpdateStep} />
          )}
          {selectedStep.type === 'conditional' && (
            <ConditionalStepProperties step={selectedStep} onUpdate={onUpdateStep} allSteps={allSteps} />
          )}
          {selectedStep.type === 'exit' && (
            <p className="text-sm text-gray-500 dark:text-gray-400">
              The journey ends here. Nothing further is sent and the enrolment is marked
              complete.
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

// Branch target selector. Only picks among steps that already exist -- the old
// "add a step from here" options set a sentinel id that nothing resolved, which
// silently dropped the branch on save.
const FlowStepSelect: React.FC<{
  step: CampaignStep;
  allSteps: CampaignStep[];
  field: 'yesStepId' | 'noStepId';
  onUpdate: (step: CampaignStep) => void;
  endLabel?: string;
  focusRing?: string;
}> = ({ step, allSteps, field, onUpdate, endLabel = 'End journey', focusRing = 'focus:ring-blue-500' }) => {
  const availableSteps = allSteps.filter(s => s.id !== step.id);
  const value = (step[field] as string | undefined) || '';
  return (
    <select
      value={value}
      onChange={(e) => onUpdate({ ...step, [field]: e.target.value || undefined })}
      className={`w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 ${focusRing} focus:border-transparent`}
    >
      <option value="">{endLabel}</option>
      {availableSteps.map(s => (
        <option key={s.id} value={s.id}>Step {s.order}: {s.data?.stepName || STEP_LABEL[s.type]}</option>
      ))}
    </select>
  );
};

// WhatsApp Step Properties
interface WhatsAppStepPropertiesProps {
  step: CampaignStep;
  onUpdate: (step: CampaignStep) => void;
  waTemplates: WhatsAppCloudTemplate[];
}

const WhatsAppStepProperties: React.FC<WhatsAppStepPropertiesProps> = ({ step, onUpdate, waTemplates }) => {
  const selected = waTemplates.find(t => t.id === step.data?.waTemplateId);
  const params: { index: number; variable: string }[] = step.data?.waParams || [];

  // The CRM lists crm_* templates only, but steps built before that rule can still
  // point at a marketing template. Without an option for it the select falls back to
  // "Select template…", which reads as "nobody ever configured this step" while the
  // step data quietly keeps sending the old template. Render the stored name as a
  // disabled option instead, so the screen says what the step actually does.
  const storedName: string = step.data?.waTemplateName || '';
  // Guarded on waTemplates.length so this never fires during the initial load,
  // when the list is legitimately empty and nothing is 'missing' yet.
  const storedIsHidden = !!storedName && !selected && waTemplates.length > 0;

  const handleFieldChange = (field: string, value: any) => {
    onUpdate({ ...step, data: { ...(step.data || {}), [field]: value } });
  };

  const handleTemplateChange = (id: number) => {
    const tmpl = waTemplates.find(t => t.id === id);
    onUpdate({
      ...step,
      data: {
        ...(step.data || {}),
        waTemplateId: id || null,
        waTemplateName: tmpl?.element_name || '',
        waLanguage: tmpl?.language || 'en',
        waParams: (tmpl?.parameters || []).map(p => ({ index: p.index, variable: '' })),
      },
    });
  };

  const handleParamChange = (index: number, variable: string) => {
    const next = params.map(p => (p.index === index ? { ...p, variable } : p));
    handleFieldChange('waParams', next);
  };

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Step Name</label>
        <input
          type="text"
          value={step.data?.stepName || ''}
          onChange={(e) => handleFieldChange('stepName', e.target.value)}
          className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent"
          placeholder="e.g., Delivery follow-up"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">WhatsApp Template *</label>
        {waTemplates.length === 0 ? (
          <p className="text-xs text-amber-600 dark:text-amber-400">
            No approved templates found. Sync them in Settings → WhatsApp Templates.
          </p>
        ) : (
          <select
            value={storedIsHidden ? '__hidden__' : (step.data?.waTemplateId || '')}
            onChange={(e) => handleTemplateChange(parseInt(e.target.value) || 0)}
            className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent"
          >
            <option value="">Select template...</option>
            {storedIsHidden && (
              <option value="__hidden__" disabled>{storedName} (not a CRM template)</option>
            )}
            {waTemplates.map((t) => (
              <option key={t.id} value={t.id}>{t.element_name} ({t.language})</option>
            ))}
          </select>
        )}
      </div>

      {storedIsHidden && (
        <p className="text-xs text-amber-600 dark:text-amber-400">
          This step still sends <span className="font-mono">{storedName}</span>, which is
          not a CRM template and is no longer listed. It keeps working as-is — pick a
          <span className="font-mono"> crm_</span> template above to change it.
        </p>
      )}

      {/* Body preview */}
      {selected && (
        <div className="p-3 rounded-lg" style={{ background: '#e5ded8' }}>
          <div className="bg-white rounded-lg rounded-tl-none shadow-sm p-2.5 text-xs text-gray-800 whitespace-pre-wrap leading-relaxed">
            {selected.body.split(/(\{\{\s*\d+\s*\}\})/g).map((part, i) =>
              /\{\{\s*\d+\s*\}\}/.test(part)
                ? <span key={i} className="px-1 rounded bg-green-100 text-green-700 font-medium">{part}</span>
                : <React.Fragment key={i}>{part}</React.Fragment>
            )}
          </div>
        </div>
      )}

      {/* Parameter mapping */}
      {selected && params.length > 0 && (
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Map template parameters</label>
          <div className="space-y-2">
            {params.map((p) => (
              <div key={p.index} className="flex items-center gap-2">
                <span className="w-12 flex-shrink-0 text-xs font-mono px-2 py-1.5 rounded bg-green-100 text-green-700 text-center">{`{{${p.index}}}`}</span>
                <select
                  value={p.variable}
                  onChange={(e) => handleParamChange(p.index, e.target.value)}
                  className="flex-1 px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent"
                >
                  <option value="">Select variable…</option>
                  {JOURNEY_VARIABLES.map((v) => (
                    <option key={v.key} value={v.key}>{v.label}</option>
                  ))}
                </select>
              </div>
            ))}
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            Each parameter is filled from the enrollment's data when the message sends.
          </p>
        </div>
      )}

    </div>
  );
};

// Agent Call Reminder Properties
// The dialer is not wired to journeys, so this raises a call task for an agent rather
// than placing a call. Routing reuses the Round Robin engine's bucket caps.
interface AgentCallStepPropertiesProps {
  step: CampaignStep;
  onUpdate: (step: CampaignStep) => void;
}

const AgentCallStepProperties: React.FC<AgentCallStepPropertiesProps> = ({ step, onUpdate }) => {
  const handleFieldChange = (field: string, value: any) => {
    onUpdate({ ...step, data: { ...(step.data || {}), [field]: value } });
  };

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Step Name</label>
        <input
          type="text"
          value={step.data?.stepName || ''}
          onChange={(e) => handleFieldChange('stepName', e.target.value)}
          className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-teal-500 focus:border-transparent"
          placeholder="e.g., Reorder reminder call"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Assign to team</label>
        <select
          value={step.data?.rrBucket || 'reorder'}
          onChange={(e) => handleFieldChange('rrBucket', e.target.value)}
          className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-teal-500 focus:border-transparent"
        >
          <option value="reorder">Reorder team</option>
          <option value="abandoned_cart">Abandoned cart team</option>
          <option value="rto">RTO team</option>
          <option value="ndr">Failed delivery team</option>
        </select>
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
          Routed by Round Robin, respecting that team's daily caps and agent availability.
        </p>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Note for the agent <span className="text-gray-400 font-normal">(optional)</span>
        </label>
        <textarea
          value={step.data?.note || ''}
          onChange={(e) => handleFieldChange('note', e.target.value)}
          rows={2}
          className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-teal-500 focus:border-transparent"
          placeholder="e.g., Check how they are finding the product and offer a refill"
        />
      </div>

      <div className="p-3 bg-teal-50 dark:bg-teal-900/20 rounded-lg">
        <p className="text-xs text-teal-800 dark:text-teal-200">
          Raises a call reminder for an agent. The system does not dial \u2014 the agent
          places the call themselves, and the outcome feeds any branch that follows.
        </p>
      </div>

    </div>
  );
};

// Delay Step Properties
interface DelayStepPropertiesProps {
  step: CampaignStep;
  onUpdate: (step: CampaignStep) => void;
}

const DelayStepProperties: React.FC<DelayStepPropertiesProps> = ({ step, onUpdate }) => {
  const handleFieldChange = (field: string, value: any) => {
    onUpdate({
      ...step,
      data: { ...(step.data || { delayValue: 1, delayUnit: 'days' }), [field]: value }
    });
  };

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Delay Duration
        </label>
        <div className="flex gap-2">
          <input
            type="number"
            value={step.data?.delayValue || 1}
            onChange={(e) => handleFieldChange('delayValue', Math.max(1, parseInt(e.target.value) || 1))}
            className="flex-1 px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="1"
            min="1"
          />
          <select
            value={step.data?.delayUnit || 'days'}
            onChange={(e) => handleFieldChange('delayUnit', e.target.value)}
            className="px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="minutes">Minutes</option>
            <option value="hours">Hours</option>
            <option value="days">Days</option>
            <option value="weeks">Weeks</option>
          </select>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Measured from
        </label>
        <select
          value={step.data?.delayFrom || 'previous_step'}
          onChange={(e) => handleFieldChange('delayFrom', e.target.value)}
          className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        >
          {DELAY_ANCHORS.map((a) => (
            <option key={a.value} value={a.value}>{a.label}</option>
          ))}
        </select>
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
          Wait {step.data?.delayValue || 1} {step.data?.delayUnit || 'days'} from{' '}
          {DELAY_ANCHORS.find((a) => a.value === (step.data?.delayFrom || 'previous_step'))?.label}.
        </p>
        {['order_delivered', 'reorder_due_date'].includes(step.data?.delayFrom) && (
          <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
            Needs a trigger that supplies an order. With a manual trigger this step falls
            back to waiting from the previous step.
          </p>
        )}
      </div>

    </div>
  );
};

// Conditional Step Properties
interface ConditionalStepPropertiesProps {
  step: CampaignStep;
  onUpdate: (step: CampaignStep) => void;
  allSteps: CampaignStep[];
}

const ConditionalStepProperties: React.FC<ConditionalStepPropertiesProps> = ({ step, onUpdate, allSteps }) => {
  const handleFieldChange = (field: string, value: any) => {
    onUpdate({
      ...step,
      data: { ...(step.data || { conditionType: 'call_connected', waitHours: 24 }), [field]: value }
    });
  };

  const conditionType = step.data?.conditionType || 'call_connected';
  const meta = CONDITION_TYPES.find((c) => c.value === conditionType);

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Branch on
        </label>
        <select
          value={conditionType}
          onChange={(e) => handleFieldChange('conditionType', e.target.value)}
          className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        >
          {CONDITION_TYPES.map((c) => (
            <option key={c.value} value={c.value}>{c.label}</option>
          ))}
        </select>
        {meta && (
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{meta.help}</p>
        )}
      </div>

      {conditionType === 'disposition_is' && (
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Disposition option ID
          </label>
          <input
            type="number"
            value={step.data?.dispositionOptionId ?? ''}
            onChange={(e) => handleFieldChange(
              'dispositionOptionId', e.target.value ? parseInt(e.target.value) : null)}
            className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="e.g., 284"
          />
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            The disposition tree node to match. A picker replaces this once the
            dispositions module lands.
          </p>
        </div>
      )}

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          How long to wait for it (hours)
        </label>
        <input
          type="number"
          value={step.data?.waitHours || 24}
          onChange={(e) => handleFieldChange('waitHours', Math.max(1, parseInt(e.target.value) || 24))}
          className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          min="1"
          placeholder="24"
        />
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
          If it hasn't happened within {step.data?.waitHours || 24} hours, the NO path is
          taken. Without a timeout an enrolment would wait forever.
        </p>
      </div>

      <div className="space-y-3">
        <div className="p-3 border-2 border-green-200 dark:border-green-800 rounded-lg">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-2 h-2 bg-green-500 rounded-full"></div>
            <span className="text-sm font-medium text-gray-900 dark:text-white">YES Path</span>
          </div>
          <FlowStepSelect step={step} allSteps={allSteps} field="yesStepId" onUpdate={onUpdate} endLabel="End campaign" focusRing="focus:ring-green-500" />
          {step.yesStepId && !step.yesStepId.startsWith('__') && (
            <p className="text-xs text-green-600 dark:text-green-400 mt-1">
              ✓ YES path configured
            </p>
          )}
        </div>

        <div className="p-3 border-2 border-red-200 dark:border-red-800 rounded-lg">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-2 h-2 bg-red-500 rounded-full"></div>
            <span className="text-sm font-medium text-gray-900 dark:text-white">NO Path</span>
          </div>
          <FlowStepSelect step={step} allSteps={allSteps} field="noStepId" onUpdate={onUpdate} endLabel="End campaign" focusRing="focus:ring-red-500" />
          {step.noStepId && !step.noStepId.startsWith('__') && (
            <p className="text-xs text-red-600 dark:text-red-400 mt-1">
              ✓ NO path configured
            </p>
          )}
        </div>
      </div>

      <div className="p-3 bg-orange-50 dark:bg-orange-900/20 rounded-lg">
        <p className="text-xs text-orange-800 dark:text-orange-200">
          The flow will branch based on whether the condition is met within the previous step.
        </p>
      </div>
    </div>
  );
};

export default CampaignBuilderModal;
